import { useEffect, useState } from "react";
import type { OpsApi } from "../useOps";

/**
 * BootLoader — full-screen overlay that holds until the basemap tiles for the
 * current view and a meaningful object count have loaded. Mirrors the prototype
 * boot gate. Dismisses on its own; a 12s safety timeout dismisses regardless.
 */
export function BootLoader({ ops }: { ops: OpsApi }) {
  const [pct, setPct] = useState(8);
  const [stat, setStat] = useState("ESTABLISHING SECURE UPLINK");
  const [done, setDone] = useState(false);

  useEffect(() => {
    const hardStop = setTimeout(() => setDone(true), 12_000);
    const poll = setInterval(() => {
      const map = ops.getMap();
      const client = ops.getClient();
      if (!map || !client) {
        setPct((p) => Math.min(24, p + 1));
        return;
      }
      const objN = client.objects.length;
      const tilesReady = typeof map._tilesReady === "function" ? map._tilesReady() : 1;
      const objTarget = ops.isLive ? 8000 : 200;
      const objFrac = Math.min(1, objN / objTarget);
      const next = 25 + tilesReady * 35 + objFrac * 40;
      setPct(Math.min(99, next));
      setStat(
        tilesReady < 0.9
          ? "RENDERING BASEMAP"
          : objFrac < 1
            ? `ACQUIRING TRACKS · ${objN.toLocaleString()}`
            : "SYNCING SIGNAL FEED",
      );
      if (tilesReady >= 0.92 && objFrac >= 1) setDone(true);
    }, 300);
    return () => {
      clearTimeout(hardStop);
      clearInterval(poll);
    };
  }, [ops]);

  return (
    <div id="boot" className={done ? "done" : ""} aria-hidden={done}>
      <div className="boot-core">
        <div className="boot-ring">
          <svg viewBox="0 0 120 120" aria-hidden="true">
            <circle className="bk" cx="60" cy="60" r="52" />
            <circle className="bk2" cx="60" cy="60" r="40" />
            <circle className="arc" cx="60" cy="60" r="52" />
            <circle className="arc2" cx="60" cy="60" r="40" />
            <line x1="60" y1="6" x2="60" y2="20" />
            <line x1="60" y1="100" x2="60" y2="114" />
            <line x1="6" y1="60" x2="20" y2="60" />
            <line x1="100" y1="60" x2="114" y2="60" />
          </svg>
          <div className="boot-pct">{Math.round(pct)}%</div>
        </div>
        <div className="boot-brand">EARLYWIRE</div>
        <div className="boot-stat">{stat}</div>
        <div className="boot-bar">
          <span style={{ width: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
}
